import type { BudgetAssumptions } from "./BudgetAssumptionsTypes";

export interface BudgetI {
  budget_id: number;
  document_id: string;
  currency: string;
  estimated_shoot_days: number;
  rerun_params?: BudgetAssumptions | null;
  /** Set by the API when saved rerun_params were adjusted so a re-send passes the rerun limits. */
  rerun_params_notice?: string | null;
  assumptions: { name: string; value: string }[];
  tax_incentives?: TaxIncentive[] | null;
  contingency_percentage: number;
  contingency_actual: number | null;
  contingency_locked: boolean;
  locked?: boolean;
  bond_fee_percentage: number;
  created_at: string;
  updated_at: string;
  categories: BudgetCategory[];
  document?: {
    project?: {
      name: string;
      workspace?: {
        name: string;
      };
    };
  };
}

export interface TaxIncentive {
  location_name: string;
  incentives: string;
}

export interface BudgetVersions {
  budget_id: number;
  primary: boolean;
  created_at: string;
  version_name: string;
  locked?: boolean;
  currency?: string;
  grand_total?: number;
  quota?: {
    used: number;
    max: number;
  };
}

export interface BudgetCategory {
  budget_category_id: number;
  budget_id: number;
  name: string;
  /** Explicit ATL/BTL section when provided by the API. */
  budget_section?: "atl" | "btl" | null;
  sub_categories: BudgetSubCategory[];
  fringe_account_number?: string;
  sort_order?: number;
  /** Whether a targeted AI rerun can regenerate this category (budget tree API). */
  rerun_eligibility?: { regenerable: boolean; reason: string | null };
}

export interface BudgetSubCategory {
  budget_sub_category_id: number;
  budget_category_id: number;
  name: string;
  account_number: string;
  locked: boolean;
  importance: number | null;
  items: BudgetItem[];
  sort_order?: number;
}

export enum BudgetItemSource {
  AI = "ai",
  TEMPLATE = "template",
  IMPORT = "import",
  MANUAL = "manual",
}

export interface BudgetItem {
  budget_item_id: number;
  budget_sub_category_id: number;
  parent_item_id?: number | null;
  account_number: string;
  name: string;
  unit: BudgetUnit | null;
  quantity: number | null;
  x_factor: number | null;
  rate: number | null;
  actual_amount: number | null;
  children: BudgetItem[];
  locked: boolean;
  fringes: BudgetFringe[];
  source?: BudgetItemSource;
  sort_order?: number;
}

export enum BudgetFringeCeilingType {
  INDIVIDUAL = "individual",
  AGGREGATE = "aggregate",
}

export enum BudgetFringeUnit {
  PERCENT = "percent",
  WEEKS = "weeks",
  DAYS = "days",
  HOURS = "hours",
}

export interface BudgetFringe {
  budget_fringe_id: number;
  document_id: string;
  account_number: string;
  name: string;
  unit: BudgetFringeUnit;
  rate: number;
  ceiling: number | null;
  ceiling_type: BudgetFringeCeilingType;
  items?: BudgetItem[];
}

export interface BudgetTotal {
  name: string;
  estimate: number;
  actual_amount: number;
  variance: number;
}

export interface BudgetContingency {
  contingency_percentage: number;
  locked: boolean;
  actual_amount: number | null;
}

export enum BudgetUnit {
  ALLOW = "allow",
  ACCEPT = "accept",
  DAYS = "days",
  WEEKS = "weeks",
  HOURS = "hours",
  FLAT = "flat",
}

export const unitsValues = [
  {
    label: "Allow",
    value: BudgetUnit.ALLOW,
  },
  {
    label: "Accept",
    value: BudgetUnit.ACCEPT,
  },
  {
    label: "Days",
    value: BudgetUnit.DAYS,
  },
  {
    label: "Weeks",
    value: BudgetUnit.WEEKS,
  },
  {
    label: "Hours",
    value: BudgetUnit.HOURS,
  },
  {
    label: "Flat",
    value: BudgetUnit.FLAT,
  },
] as const;

export interface BudgetUpdate {
  contingency_percentage: number;
  contingency_actual: number | null;
  contingency_locked: boolean;
}
